import { ExternalPlaidAccount, Query } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useRouter,
} from 'expo-router';
import useHasPlaidMarketingConsent, {
  PlaidMarketingConsentEntryPoint,
} from 'features/plaid/hooks/useHasPlaidMarketingConsent';
import usePlaidLinkLauncher from 'features/plaid/hooks/usePlaidLinkLauncher';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ImageSourcePropType,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FetchHomeTilesV2 } from 'services/graphql/queries/FetchHomeTilesV2';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { Snackbar } from 'shared/components/vxl/Snackbar/Snackbar';
import homeTileTypes from 'shared/constants/homeTileTypes';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { GroupedAccountsByInstitution } from 'shared/hooks/externalAccounts/useExternalAccounts';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { toTitleCase } from 'shared/utils/string/string.helpers';
import { useQuery } from 'urql';

const LinkedExternalAccounts = () => {
  const router = useRouter();
  const [hasPlaidLaunchFailed, setHasPlaidLaunchFailed] = useState(false);
  const insets = useSafeAreaInsets();
  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.LINKED_ACCOUNTS_TILE,
  ]);

  const [, refetchHomeTiles] = useQuery<Query>({
    query: FetchHomeTilesV2,
    variables: {
      tileTypes: homeTileTypes,
    },
    requestPolicy: 'network-only',
  });

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'externalAccounts',
  });

  const {
    externalAccounts,
    externalAccountsErrorModel,
    externalAccountsLoading,
    refreshExternalAccounts,
    startPlaidOrShowConsent,
  } = useHasPlaidMarketingConsent({
    entryPoint: PlaidMarketingConsentEntryPoint.LinkedAccounts,
  });

  const linkAccountPressEvent = usePressableAnalytics({
    analyticsId: 'linkAccountClicked',
    customAttributes: {
      state: externalAccounts?.length ? 'linked' : 'unlinked',
      cta: 'Link an account',
    },
  });

  const {
    startLaunch,
    cancelLaunch,
    error: plaidError,
  } = usePlaidLinkLauncher(PlaidMarketingConsentEntryPoint.LinkedAccounts, {
    onResult: (result) => {
      if (result.status === 'error') {
        setHasPlaidLaunchFailed(true);
        return;
      }

      if (result.status !== 'success') {
        return;
      }

      refreshExternalAccounts();

      if (externalAccounts?.length === 0) {
        refetchHomeTiles();
      } else {
        refreshTiles();
      }
    },
  });

  const handleLinkAccount = () => {
    setHasPlaidLaunchFailed(false);
    startPlaidOrShowConsent(startLaunch);
    linkAccountPressEvent();
  };

  const handleDismissPlaidError = () => {
    setHasPlaidLaunchFailed(false);
    cancelLaunch();
  };

  const shouldShowLoadingOverlay =
    externalAccountsLoading && !externalAccounts?.length;
  const shouldShowPlaidError = hasPlaidLaunchFailed || !!plaidError;
  const plaidErrorMessage = hasPlaidLaunchFailed
    ? translatedText('linkAccountFailed')
    : translatedText('linkAccountError');

  useFocusEffect(
    useCallback(() => {
      refreshExternalAccounts();
    }, [refreshExternalAccounts]),
  );

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('externalAccountsAndCards'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={() => navigateBack()}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={shouldShowLoadingOverlay}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          externalAccountsErrorModel ? (
            <ErrorScreen {...externalAccountsErrorModel} />
          ) : null
        }
      />

      {externalAccounts?.length ? (
        <ScrollView contentContainerStyle={styles.scrollContainer}>
          <View>
            {externalAccounts.map(
              (bank: GroupedAccountsByInstitution, index: number) => {
                return (
                  <React.Fragment key={bank.institution}>
                    {index > 0 ? (
                      <View style={styles.institutionDivider} />
                    ) : null}
                    <SectionDivider labelText={bank.institution} />
                    {bank?.accounts?.map(
                      (
                        externalAccount: ExternalPlaidAccount,
                        externalAccountIndex: number,
                      ) => {
                        const formattedPrimaryCopy = `${externalAccount.institution.name} ${toTitleCase(externalAccount?.product)} • ${externalAccount?.accountNumberLast4}`;

                        return (
                          <React.Fragment key={externalAccount.id}>
                            {externalAccountIndex > 0 ? (
                              <View style={styles.dividerContainer}>
                                <HairLineDivider />
                              </View>
                            ) : null}
                            <ListItem
                              accessibilityLabel="external account"
                              key={externalAccount.id}
                              primaryCopy={formattedPrimaryCopy}
                              secondaryCopy={
                                externalAccount?.currentBalance?.formatted
                              }
                              onPress={() => {
                                router.push({
                                  pathname: '/myvaro/external-accounts/[id]',
                                  params: { id: externalAccount?.id },
                                });
                              }}
                              leadingAccessory={{
                                id: '1',
                                name: VXLComponentId.ICON_AVATAR,
                                props: {
                                  iconAccessibilityLabel: 'Bank icon',
                                  imageSource: {
                                    uri: externalAccount.iconUrl,
                                  } as ImageSourcePropType,
                                  variant: 'MD',
                                },
                              }}
                              analyticsId="externalAccountClicked"
                              customAttributes={{
                                institution_name:
                                  externalAccount.institution.name,
                                account_type:
                                  externalAccount.product.toLocaleLowerCase(),
                              }}
                            />
                          </React.Fragment>
                        );
                      },
                    )}
                  </React.Fragment>
                );
              },
            )}
          </View>

          <View
            style={[
              styles.buttonWrapper,
              { paddingBottom: insets.bottom + theme.spacing.m },
            ]}
          >
            <Button
              label={translatedText('buttons.linkAccount')}
              onPress={handleLinkAccount}
              type="SECONDARY"
            />
          </View>
        </ScrollView>
      ) : (
        <EmptyStateScreen
          bodyText={translatedText('emptyState.description')}
          headlineText={translatedText('emptyState.heading')}
          illustrationKey="BANK_A"
          actionButtonText={translatedText('buttons.linkAccount')}
          actionOnPress={handleLinkAccount}
        />
      )}

      {shouldShowPlaidError ? (
        <Snackbar
          text={plaidErrorMessage}
          buttonOnPress={handleDismissPlaidError}
          disableAutoHide
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
  dividerContainer: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  institutionDivider: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
});

export default LinkedExternalAccounts;
